import dayjs from 'dayjs';
import { ComponentPropsWithoutRef, FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  Avatar,
  Button,
  Divider,
  Link as MUILink,
  Paper,
  Typography,
} from '@mui/material';
import { cyan } from '@mui/material/colors';
import { Box, Stack } from '@mui/system';

import { AppPath } from '@/shared/constants/path';
import Customer from '@/shared/types/customer.type';

type CustomerDetailsPanelProps = {
  customer: Customer;
  isBillingAddressVisible?: boolean;
  isShippingAddressVisible?: boolean;
  paperProps?: ComponentPropsWithoutRef<typeof Paper>;
  onEditCustomer?: () => void;
  addressCustomRender?: () => ReactNode;
};

const CustomerDetailsPanel: FC<CustomerDetailsPanelProps> = ({
  customer,
  isBillingAddressVisible = false,
  isShippingAddressVisible = false,
  paperProps,
  onEditCustomer,
  addressCustomRender,
}) => {
  const { t } = useTranslation(['customers', 'forms', 'common']);

  return (
    <Paper elevation={3} sx={{ py: 2 }} {...paperProps}>
      <Stack direction='row' spacing={2} sx={{ px: 2, alignItems: 'center' }}>
        <Avatar
          alt={customer.display_full_name || customer.email_address}
          src={''}
          sx={{
            width: 80,
            height: 80,
          }}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            width: '100%',
          }}
        >
          {customer.display_full_name ? (
            <MUILink
              component={Link}
              to={AppPath.Customers + `/${customer.in_customer_id}`}
              variant='body1'
              underline='none'
              noWrap
            >
              {customer.display_full_name}
            </MUILink>
          ) : (
            <Typography variant='body1' color='text.secondary' noWrap>
              {t('customers:nameIsNotAvailable')}
            </Typography>
          )}

          <Stack
            direction='row'
            spacing={1}
            sx={{
              width: '100%',
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              alignItems: 'center',
            }}
          >
            <MUILink
              underline='none'
              variant='body1'
              href={`mailto:${customer.email_address}`}
              sx={{ color: cyan[600] }}
              noWrap
            >
              {customer.email_address}
            </MUILink>

            {onEditCustomer && (
              <Button onClick={onEditCustomer} variant='text' size='small'>
                {t('customers:editCustomer')}
              </Button>
            )}
          </Stack>

          {customer.phone_number ? (
            <Typography variant='body1' noWrap>
              {t('forms:phone')}: {customer.phone_number}
            </Typography>
          ) : (
            <Typography variant='body1' color='text.secondary' noWrap>
              {t('customers:phoneNumberIsNotAvailable')}
            </Typography>
          )}

          <Typography variant='body1' color='text' noWrap>
            {t('forms:ts_created')}:{' '}
            {dayjs(customer.ts_created).format('YYYY-MM-DD HH:mm A')}
          </Typography>
        </Box>
      </Stack>

      {(isBillingAddressVisible || isShippingAddressVisible) && (
        <Divider sx={{ my: 2 }} />
      )}

      {addressCustomRender ? (
        addressCustomRender()
      ) : (
        <>
          <Stack spacing={2}>
            {isBillingAddressVisible && (
              <Stack spacing={0} px={2}>
                <Typography variant='body1' sx={{ mb: 1 }}>
                  {t('customers:billingAddress')}
                </Typography>

                {!customer.billing_address &&
                  !customer.billing_address2 &&
                  !customer.billing_city &&
                  !customer.billing_state &&
                  !customer.billing_postcode && (
                    <Typography variant='body2' color='text.secondary'>
                      {t('customers:addressIsNotAvailable')}
                    </Typography>
                  )}

                <Stack spacing={1} direction='row'>
                  <Typography variant='body2' color='text.secondary'>
                    {customer.billing_address}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {customer.billing_address2}
                  </Typography>
                </Stack>

                <Typography variant='body2' color='text.secondary'>
                  {customer.billing_city}
                </Typography>

                <Stack spacing={1} direction='row'>
                  <Typography variant='body2' color='text.secondary'>
                    {customer.billing_state}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {customer.billing_postcode}
                  </Typography>
                </Stack>
              </Stack>
            )}

            {isShippingAddressVisible && (
              <>
                <Divider sx={{ my: 2 }} />

                <Stack spacing={0} px={2}>
                  <Typography variant='body1' sx={{ mb: 1 }}>
                    {t('customers:shippingAddress')}
                  </Typography>

                  {!customer.delivery_address &&
                    !customer.delivery_address2 &&
                    !customer.delivery_city &&
                    !customer.delivery_state &&
                    !customer.delivery_postcode && (
                      <Typography variant='body2' color='text.secondary'>
                        {t('customers:addressIsNotAvailable')}
                      </Typography>
                    )}

                  <Stack spacing={1} direction='row'>
                    <Typography variant='body2' color='text.secondary'>
                      {customer.delivery_address}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      {customer.delivery_address2}
                    </Typography>
                  </Stack>

                  <Typography variant='body2' color='text.secondary'>
                    {customer.delivery_city}
                  </Typography>

                  <Stack spacing={1} direction='row'>
                    <Typography variant='body2' color='text.secondary'>
                      {customer.delivery_state}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      {customer.delivery_postcode}
                    </Typography>
                  </Stack>
                </Stack>
              </>
            )}
          </Stack>
        </>
      )}
    </Paper>
  );
};

export default CustomerDetailsPanel;
